import { motion } from 'framer-motion'
import { PlanCard } from '@/components/pricing-plans/PlanCard.jsx'
import { usePaddle } from '@/hooks/usePaddle.jsx'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import { cancelSubscription, getSubscriptionOfAccount } from '@/services/api.js'
import { useEffect, useState } from 'react'
import { usePaymentContext } from '@/context-state/PaymentContext.jsx'
import { useCurrentAccount } from '@/hooks/useCurrentAccount.jsx'
import { TypographySmall, TypographySmallMuted } from '@/components/ui/typography.jsx'
import { Switch } from '@/components/ui/switch.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { CircleLoader } from '@/components/loader/CircleLoader.jsx'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { Button } from '@/components/ui/button.jsx'
import { toggleVerifyPayment } from '@/hooks/useVerifyPayment.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { AnalyticsService } from '@/services/analytics.js'
import * as API from '@/services/api.js'

const generateRandomId = (length = 8) => {
    let id = ''
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < length; i++) {
        id += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return id
}


export const PricingPlans = ({ onCheckoutOpened, onSubscriptionCancelled }) => {


    const [isCancellingSubscription, setIsCancelingSubscription] = useState(false)
    const [isApproveCancelDialogOpen, setIsApproveCancelDialogOpen] = useState(false)

    const [cycle, setCycle] = useState('annually')
    const { plans, setSubscription, subscription } = usePaymentContext()
    const { account } = useCurrentAccount()
    const { user } = useAuthContext()
    const transactionId = account?.customCheckout

    const handlePaddleEvents = (event) => {
        if (event.name === 'checkout.loaded') {
            AnalyticsService.track('checkout-opened', event.data)
        }

        if (event.name === 'checkout.payment.initiated') {
            AnalyticsService.track('checkout-payment-initialized', event.data)
        }

        if (event.name === 'checkout.closed') {
            if (event.data.status === 'completed') {
                AnalyticsService.track('checkout-closed-on-complete', event.data)
                toggleVerifyPayment(true)
                return
            }
            AnalyticsService.track('checkout-closed-before-complete', event.data)
        }
    }

    const paddleClient = usePaddle(handlePaddleEvents)

    const openCustomCheckout = () => {
        try {
            const paddleSessionId = generateRandomId()
            localStorage.setItem('paddleSessionId', paddleSessionId) // We do this because we need to verify this identifier later on the 'VerifyPayment'
            paddleClient?.Checkout.open({
                transactionId,
                customData: { account: account._id, sessionId: paddleSessionId, user: user._id },
            })
            if (onCheckoutOpened) {
                onCheckoutOpened()
            }
        } catch (e) {
            handleError(e)
            toast.error(e.message)
            console.error(e)
        }
    }

    const openCheckout = async (chosenPlan, chosenCycle) => {
        try {

            // Check if needs Shopify checkout (This logic should be away from here in a seprate service or something like that)
            if (account.type === 'store' && account.ecomProvider === 'shopify') {
                const planId = chosenPlan._id
                // We set those to local storage to verify the plan id and cycle later on the 'VerifyShopifyPaymentPage'
                localStorage.setItem('shop', account.shopDomain)
                localStorage.setItem('planId', planId)
                // localStorage.setItem('billingCycle', chosenCycle)
                const callbackUrl = `${window.location.origin}/payment/shopify`
                const { data } = await API.initShopifyCheckout({ planId, callbackUrl, billingCycle: chosenCycle })
                window.location.replace(data.confirmationUrl)
                return
            }



            // --------- Continue normal flow with Paddle ---------

            // Custom checkout 
            if (transactionId) {
                openCustomCheckout()
                return
            }

            // Regular flow
            let paddlePriceId
            const relevantBillingCyclesPrices = chosenPlan.providers?.paddle?.prices?.[chosenCycle]

            if (relevantBillingCyclesPrices) {
                const activePrice = relevantBillingCyclesPrices.find(price => price.active)
                if (activePrice) {
                    paddlePriceId = activePrice.price_id
                }
            }

            if (!paddlePriceId) {
                // It's important for us to track this issue if it's occurred
                handleError(new Error('Missing plan price id'))
                AnalyticsService.track('missing-price-id-on-checkout', { plan: chosenPlan._id, cycle: chosenCycle })
                toast.error("There was an error initializing the checkout, please contact support")
                console.warn(`missing plan price id on plan - ${chosenPlan._id}. chosenCycle - ${chosenCycle}`)
                return
            }

            const paddleSessionId = generateRandomId()
            localStorage.setItem('paddleSessionId', paddleSessionId) // We do this because we need to verify this identifier later on the 'VerifyPayment'

            paddleClient?.Checkout.open({
                // settings: {
                // variant: "one-page"
                // displayMode: "inline"
                // },
                // transactionId: 'txn_01jnbtcntf3qke09twaaf4zvy5' - to create custom URL
                customData: { account: account._id, sessionId: paddleSessionId, user: user._id },
                items: [{ priceId: paddlePriceId }]
            })
            if (onCheckoutOpened) {
                onCheckoutOpened()
            }

        } catch (e) {
            handleError(e)
            toast.error(e.message)
            console.error(e)
        }
    }

    const onCancelSubscription = async () => {
        try {
            setIsApproveCancelDialogOpen(false)
            setIsCancelingSubscription(true)
            await cancelSubscription(subscription._id)

            // Poll it the dumb way...
            for (let i = 0; i < 50; i++) {
                await new Promise(resolve => setTimeout(resolve, 1000))
                const { data } = await getSubscriptionOfAccount()
                if (data.status === 'free') {
                    setSubscription(data)
                    toast.info('Subscription Canceled Successfully')
                    break
                }
            }

            if (onSubscriptionCancelled) {
                onSubscriptionCancelled()
            }

        } catch (e) {
            handleError(e)
            toast.error(e.message)
            console.error(e)
        } finally {
            setIsCancelingSubscription(false)
        }
    }

    useEffect(() => {
        if (transactionId && paddleClient) {
            openCustomCheckout()
        }
    }, [transactionId, paddleClient])

    return (
        <div className="flex flex-col">

            <Dialog open={isApproveCancelDialogOpen} onOpenChange={setIsApproveCancelDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Are you sure?</DialogTitle>
                        <DialogDescription>Approve you want to cancel your subscription</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <div className="flex justify-between gap-2">
                            <Button variant="outline" onClick={() => setIsApproveCancelDialogOpen(false)}>Keep Subscription</Button>
                            <Button variant="destructive" onClick={onCancelSubscription}>Cancel Subscription</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {
                isCancellingSubscription && (
                    <div className="fixed bg-white/80 w-full h-full top-0 left-0 z-50 flex flex-col items-center justify-center gap-2">
                        <CircleLoader />
                        Canceling Subscription...
                    </div>
                )
            }
            <div className="flex items-center gap-2 justify-center relative py-4">
                <TypographySmall>Monthly</TypographySmall>

                <Switch
                    checked={cycle === 'annually'}
                    onCheckedChange={() => setCycle(cycle === 'annually' ? 'monthly' : 'annually')}
                />
                <TypographySmall>Yearly</TypographySmall>
                <Badge variant="secondary" className="absolute bg-secondary-100/50 border-secondary text-secondary hover:bg-secondary-100/50 ml-[300px] w-[140px] text-center">More than 20% off!</Badge>
            </div>
            <div className="flex items-center justify-center gap-4">
                {
                    plans
                        .sort((a, b) => a.order - b.order)
                        .map((plan, index) => {
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="relative h-full"
                                    transition={{ duration: `0.${index + 1}` }}
                                >
                                    <PlanCard
                                        cycle={cycle}
                                        plan={plan}
                                        currentSubscription={subscription}
                                        onPlanChosen={openCheckout}
                                    />
                                </motion.div>
                            )
                        })
                }
            </div>
            {
                subscription.status !== 'free' && (
                    <div className="mt-5 w-fit m-auto">
                        <TypographySmallMuted
                            onClick={() => setIsApproveCancelDialogOpen(true)}
                            className="hover:underline cursor-pointer"
                        >
                            Cancel Subscription
                        </TypographySmallMuted>
                    </div>
                )
            }
        </div>

    )
}